import type { ReactNode } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  formatAuditVisibleDateUtc,
  formatAuditVisibleUtc,
  formatDateTimeIST,
} from "../utils/format-audit-datetime";

interface AuditTimestampProps {
  value: string | Date;
  children?: ReactNode;
  className?: string;
}

/** Visible label in UTC; tooltip shows the same instant in IST. */
export function AuditTimestamp({ value, children, className }: AuditTimestampProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "cursor-default underline decoration-dotted decoration-gray-300 underline-offset-2",
            className
          )}
        >
          {children ?? formatAuditVisibleUtc(value)}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        {formatDateTimeIST(value)}
      </TooltipContent>
    </Tooltip>
  );
}

interface AuditDateRangeProps {
  start: string | Date;
  end: string | Date;
  children?: ReactNode;
  className?: string;
}

/** Date range label in UTC; tooltip shows IST start/end. */
export function AuditDateRange({ start, end, children, className }: AuditDateRangeProps) {
  const startIst = formatDateTimeIST(start);
  const endIst = formatDateTimeIST(end);
  const tooltip =
    startIst === endIst ? startIst : `${startIst} – ${endIst}`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "cursor-default underline decoration-dotted decoration-gray-300 underline-offset-2",
            className
          )}
        >
          {children ?? (
            <>
              {formatAuditVisibleDateUtc(start)} – {formatAuditVisibleDateUtc(end)}
            </>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs max-w-xs">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}
